import { motion } from "framer-motion";
import { ScanLine, Fingerprint, BookOpen, ArrowRight, Lock, QrCode } from "lucide-react";

const MACHINE_IMG =
  "https://images.unsplash.com/photo-1717386255767-52643970d483?crop=entropy&cs=srgb&fm=jpg&q=85&w=700";

const CONCEPTS = [
  { testid: "qr-step-escanea", icon: ScanLine, title: "Escanea", text: "Apunta con la cámara del móvil al QR de la máquina." },
  { testid: "qr-step-identifica", icon: Fingerprint, title: "Identifica", text: "El pasaporte de esa máquina se abre al instante." },
  { testid: "qr-step-consulta", icon: BookOpen, title: "Consulta", text: "Manuales, esquemas, fotos e historial, delante del equipo." },
];

export default function QrSection() {
  return (
    <section className="bg-zinc-950 text-white" data-testid="qr-experience-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 lg:py-36">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-widest font-medium text-zinc-400 mb-5">QR en la máquina</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            La información de la máquina, en la propia máquina.
          </h2>
          <p className="text-lg text-zinc-400 leading-relaxed mt-5 max-w-2xl">
            Al crear una máquina generamos automáticamente su QR único. Imprímelo, colócalo sobre
            el equipo y cualquier persona autorizada accederá a su pasaporte escaneándolo.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 lg:gap-8 mt-16 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden border border-zinc-800 min-h-[260px]"
          >
            <img src={MACHINE_IMG} alt="Máquina industrial" className="absolute inset-0 h-full w-full object-cover opacity-70" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/20 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <p className="text-sm font-medium text-zinc-200">Máquina industrial</p>
              <span className="h-14 w-14 rounded-xl bg-white p-2 flex items-center justify-center shadow-lg">
                <QrCode size={36} className="text-zinc-900" />
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="rounded-3xl border border-zinc-800 bg-zinc-900/60 p-8 flex flex-col items-center justify-center text-center min-h-[260px]"
          >
            <span className="h-16 w-16 rounded-2xl bg-white p-3 flex items-center justify-center mb-5">
              <QrCode size={40} className="text-zinc-900" />
            </span>
            <p className="text-sm text-zinc-300 font-medium">QR único de la máquina</p>
            <p className="text-xs text-zinc-500 mt-2">Generado automáticamente. Listo para imprimir y pegar.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="rounded-3xl border border-zinc-800 bg-zinc-900/60 p-6 min-h-[260px] flex flex-col"
          >
            <div className="mx-auto w-full max-w-[210px] rounded-[1.8rem] border border-zinc-700 bg-zinc-950 p-3 shadow-2xl">
              <div className="rounded-3xl bg-white text-zinc-900 p-4 h-full">
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <span className="text-[10px] font-medium text-emerald-700">Operativa</span>
                </div>
                <p className="font-heading font-bold text-sm tracking-tight">INYECTORA 01</p>
                <p className="text-[11px] text-zinc-500">ENGEL · victory 220</p>
                <div className="mt-3 space-y-1.5">
                  {["Manual de usuario", "Esquema eléctrico", "Historial"].map((t) => (
                    <div key={t} className="rounded-lg bg-zinc-100 px-2.5 py-1.5 text-[10px] font-medium text-zinc-700">
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-sm text-zinc-300 font-medium text-center mt-5">Pasaporte en tu móvil</p>
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 lg:gap-8 mt-14">
          {CONCEPTS.map((c, i) => (
            <motion.div
              key={c.testid}
              data-testid={c.testid}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="flex sm:flex-col items-start gap-4"
            >
              <span className="h-11 w-11 rounded-full border border-zinc-700 flex items-center justify-center shrink-0">
                <c.icon size={18} />
              </span>
              <div>
                <h3 className="font-heading text-xl font-semibold tracking-tight">{c.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mt-1.5">{c.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 rounded-3xl border border-zinc-800 bg-zinc-900/40 p-7 sm:p-9 flex flex-col sm:flex-row gap-5 items-start">
          <span className="h-11 w-11 rounded-full bg-zinc-800 flex items-center justify-center shrink-0">
            <Lock size={18} className="text-zinc-300" />
          </span>
          <div>
            <p className="text-zinc-200 font-medium">
              Consulta un manual, encuentra un esquema eléctrico o revisa una intervención anterior
              directamente delante de la máquina.
            </p>
            <p className="text-sm text-zinc-500 leading-relaxed mt-2">
              Sin buscar carpetas. Sin preguntar quién tiene el manual. Sin volver a la oficina.
              Los QR nunca hacen pública la información: si no tienes sesión iniciada, primero
              inicias sesión y después llegas automáticamente a la máquina que escaneaste.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
